import { useCallback, useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { AppMode } from "../../state/mode/mode.reducer";
import type { AppState } from "../../state/root.reducer";
import {
  getAllProjects,
  getAssignableProjects,
} from "../../state/workspaces/projects.selector";
import type { MyWorkspaceMetadata } from "../../state/workspaces/types";
import { useI18n } from "../../utils/hooks/use.i18n";
import { AssignToProjectStrategy } from "./assign.to.project.strategy";
import type { UseAssignWorkspaceToProjectResult } from "./types";

// FIXME: This hook is over-engineered. Refactor at the next opportunity to reduce complexity. `AssignToProjectStrategy` is likely unnecessary as well.
export const useAssignWorkspaceToProject = (
  myWorkspaceMetadata?: MyWorkspaceMetadata,
): UseAssignWorkspaceToProjectResult => {
  const dispatch = useDispatch();
  const t = useI18n("ERROR.PROJECTS.");

  const projectAssignErrorMsg = [
    t("CANNOT_ADD_WORKSPACE"),
    t("REASON_MAX_WORKSPACES"),
  ].join(" ");

  const projects = useSelector(getAllProjects);
  const assignableProjects = useSelector(getAssignableProjects);
  const isProjectAssignError = useSelector(
    (state: AppState) =>
      state.mode === AppMode.ERROR &&
      state.context.error.message === projectAssignErrorMsg,
  );

  const assignToProjectStrategy = useMemo(
    () =>
      new AssignToProjectStrategy(projects ?? [], assignableProjects, dispatch),
    [assignableProjects, dispatch, projects],
  );

  const initializeState = useCallback(
    () =>
      assignToProjectStrategy.initialSelectedOption(
        myWorkspaceMetadata?.containerId,
      ),
    [assignToProjectStrategy, myWorkspaceMetadata],
  );

  const [currentProjects, setCurrentProjects] = useState(initializeState);

  useEffect(() => {
    if (isProjectAssignError) {
      setCurrentProjects(initializeState());
    }
  }, [isProjectAssignError]);

  const projectOptions = assignToProjectStrategy.getProjectOptions();

  const projectHasChanged =
    currentProjects[0]?.value.id !== myWorkspaceMetadata?.containerId;

  const saveProject = useCallback(() => {
    const [selectedProjectOption] = currentProjects;

    if (
      !projectHasChanged ||
      !selectedProjectOption ||
      !myWorkspaceMetadata?.workspaceId
    ) {
      return;
    }

    assignToProjectStrategy.assignToProject(
      myWorkspaceMetadata.workspaceId,
      selectedProjectOption.value,
    );
  }, [currentProjects, projectHasChanged, myWorkspaceMetadata]);

  useEffect(() => {
    saveProject();
  }, [saveProject]);

  return {
    currentProjects,
    projectHasChanged,
    projectOptions,
    saveProject,
    setCurrentProjects,
  };
};
